import "./Chatbot.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Card } from "@amzn/stencil-react-components/card";
import { Chip, ChipBackgroundColor } from "@amzn/stencil-react-components/chip";
import { Col, Hr, Row, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { TextArea } from "@amzn/stencil-react-components/text-area";
import IconChevronRightMedium from "@amzn/stencil-react-icons/icons/icon-chevron-right-medium";
import IconSparklesExtraSmall from "@amzn/stencil-react-icons/icons/icon-sparkles-extra-small";
import { trackStructEvent } from "@snowplow/browser-tracker";
import { useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useChatbot } from "@/hooks/useChatbot";
import { AppContext } from "@/stores/appStore";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

import type { ChatMessage } from "../../@types/agentcore";

const formatMessage = (
  text: string,
  media?: Array<{ type: string; url: string; alt: string }>
): React.ReactNode[] => {
  return text.split("\n").flatMap((line, li) => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    // Combined regex: bold or image placeholder
    const combinedRegex = /(\*\*(.+?)\*\*|!\[([^\]]*)\]\(image:(\d+)\))/g;
    let match;
    while ((match = combinedRegex.exec(line)) !== null) {
      if (match.index > lastIndex)
        parts.push(line.slice(lastIndex, match.index));
      if (match[2]) {
        // Bold
        parts.push(<strong key={`${li}-b-${match.index}`}>{match[2]}</strong>);
      } else if (match[4] && media) {
        // Image placeholder
        const idx = parseInt(match[4], 10);
        const item = media[idx];
        if (item) {
          parts.push(
            <img
              key={`${li}-img-${idx}`}
              src={item.url}
              alt={match[3] || item.alt || ""}
              style={{
                maxWidth: "100%",
                borderRadius: "4px",
                display: "block",
                margin: "8px 0",
              }}
            />
          );
        }
      }
      lastIndex = combinedRegex.lastIndex;
    }
    if (lastIndex < line.length) parts.push(line.slice(lastIndex));
    if (li > 0) parts.unshift(<br key={`br-${li}`} />);
    return parts;
  });
};

const useTypewriter = (
  text: string,
  animate: boolean,
  speed = 30,
  onTick?: () => void
): string => {
  const [displayed, setDisplayed] = useState(animate ? "" : text);
  const words = useRef<string[]>([]);
  const idx = useRef(0);

  useEffect(() => {
    if (!animate) {
      setDisplayed(text);
      return;
    }
    words.current = text.split(/(\s+)/);
    idx.current = 0;
    setDisplayed("");
    const timer = setInterval(() => {
      idx.current++;
      setDisplayed(words.current.slice(0, idx.current).join(""));
      onTick?.();
      if (idx.current >= words.current.length) clearInterval(timer);
    }, speed);
    return () => clearInterval(timer);
  }, [text, animate, speed]);

  return displayed;
};

const MessageBubble = ({
  msg,
  animate,
  onSuggestionClick,
  onTick,
}: {
  msg: ChatMessage;
  animate: boolean;
  onSuggestionClick: (topic: string) => void;
  onTick?: () => void;
}): JSX.Element => {
  const isUser = msg.role === "user";
  const displayText = useTypewriter(msg.content, animate, 20, onTick);

  return (
    <Col padding={{ bottom: "S200" }}>
      <Row
        justifyContent={isUser ? "flex-end" : "flex-start"}
        padding={{ bottom: "S100" }}
      >
        <Text fontSize="T100" color="color.text.secondary">
          {isUser ? (
            "You"
          ) : (
            <Row gridGap={"dimensions.spacing.100"}>
              <IconSparklesExtraSmall aria-hidden /> Aiva
            </Row>
          )}
        </Text>
      </Row>
      <Row justifyContent={isUser ? "flex-end" : "flex-start"}>
        <Card
          padding="S200"
          backgroundColor={
            isUser ? "color.purple.60" : "color.surface.bg-muted"
          }
          borderRadius="dimensions.radius.300"
          maxWidth="80%"
        >
          <Text
            fontSize="T200"
            color={isUser ? "color.neutral.00" : "color.text.primary"}
          >
            {formatMessage(displayText, msg.media)}
          </Text>
        </Card>
      </Row>
      {msg.suggestedTopics && msg.suggestedTopics.length > 0 && (
        <Row gridGap="S200" flexWrap="wrap" padding={{ top: "S300" }}>
          {msg.suggestedTopics.map((topic) => (
            <Chip
              key={topic}
              backgroundColor={ChipBackgroundColor.Blue}
              onClick={() => onSuggestionClick(topic)}
            >
              {topic}
            </Chip>
          ))}
        </Row>
      )}
    </Col>
  );
};

interface ChatbotProps {
  onContactSupport?: (escalationSummary: string) => void;
}

const STEP_WELCOME_MESSAGES: Record<string, string> = {
  IdentityCheck: "chatbot-welcome-identity-check",
  AmazonUsername: "chatbot-welcome-amazon-username",
  InsertAndTestSecurityKey: "chatbot-welcome-insert-security-key",
  RegisterSecurityKey: "chatbot-welcome-register-security-key",
  ConnectVpn: "chatbot-welcome-connect-vpn",
  AmazonPassword: "chatbot-welcome-amazon-password",
  PreCacheADMobile: "chatbot-welcome-precache",
};

export const Chatbot = ({ onContactSupport }: ChatbotProps): JSX.Element => {
  const {
    messages,
    isLoading,
    sendMessage,
    escalationSummary,
    escalationInitiated,
  } = useChatbot();
  const { state } = useContext(AppContext);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();

  // Auto-redirect to IT Support when agent initiates escalation
  useEffect(() => {
    if (escalationInitiated && onContactSupport) {
      const lastMsg = messages[messages.length - 1];
      const typewriterDuration =
        lastMsg?.role === "assistant"
          ? lastMsg.content.split(/(\s+)/).length * 30
          : 0;
      const timer = setTimeout(
        () => onContactSupport(escalationSummary),
        typewriterDuration + 5000
      );
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escalationInitiated]);

  const welcomeMessageId =
    STEP_WELCOME_MESSAGES[state.currentStep?.id ?? ""] ??
    "chatbot-welcome-default";

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [messages, isLoading]);

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    void sendMessage(input);
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Col
      height="calc(100%)"
      dataTestId="chatbotContainer"
      backgroundColor="color.surface.bg-default"
      overflow="hidden"
    >
      {/* Messages */}
      <View
        flex={1}
        overflow="auto"
        padding="S300"
        role="log"
        aria-label={intl.formatMessage({ id: "chatbot-messages-label" })}
        aria-live="polite"
      >
        {messages.length === 0 && (
          <Row justifyContent="flex-start" padding={{ bottom: "S200" }}>
            <Card
              padding="S200"
              backgroundColor="color.surface.bg-muted"
              borderRadius="dimensions.radius.300"
              maxWidth="80%"
            >
              <Text fontSize="T200">
                {intl.formatMessage({ id: welcomeMessageId })}
              </Text>
            </Card>
          </Row>
        )}
        {messages.map((msg, i) => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            animate={msg.role === "assistant" && i === messages.length - 1}
            onSuggestionClick={(topic) => {
              trackStructEvent({
                category: Day1EventCategory.Chatbot,
                action: Day1EventAction.ChatbotSuggestedTopicClicked,
                property: topic,
              });
              void sendMessage(topic);
            }}
            onTick={() =>
              messagesEndRef.current?.scrollIntoView?.({ behavior: "smooth" })
            }
          />
        ))}
        {isLoading && (
          <Col padding={{ bottom: "S200" }} dataTestId="chatbotTyping">
            <Row padding={{ bottom: "S100" }}>
              <Text fontSize="T100" color="color.text.tertiary">
                <Row gridGap={"dimensions.spacing.100"}>
                  <IconSparklesExtraSmall aria-hidden /> Aiva
                </Row>
              </Text>
            </Row>
            <Row>
              <Card
                padding="S200"
                backgroundColor="color.surface.bg-muted"
                borderRadius="dimensions.radius.300"
              >
                <span className="chatbot-thinking-dots">
                  <span />
                  <span />
                  <span />
                </span>
              </Card>
            </Row>
          </Col>
        )}
        <div ref={messagesEndRef} />
      </View>

      {/* Contact support link */}
      {onContactSupport && (
        <Row justifyContent="center" padding={{ top: "S200", bottom: "S100" }}>
          <Button
            variant={ButtonVariant.Tertiary}
            onClick={() => {
              trackStructEvent({
                category: Day1EventCategory.Chatbot,
                action: Day1EventAction.ChatbotEscalationClicked,
              });
              // Fire-and-forget: record the user's intent in the conversation,
              // but don't wait on it — escalate to IT Support immediately.
              void sendMessage(
                intl.formatMessage({ id: "chatbot-contact-support-message" })
              );
              onContactSupport(escalationSummary);
            }}
            dataTestId="chatbotContactSupport"
          >
            <FormattedMessage id="chatbot-contact-support" />
          </Button>
        </Row>
      )}

      {/* Input area */}
      <Hr />
      <Row alignItems="center" padding="S300" gridGap="S200">
        <TextArea
          id="chatbot-input"
          aria-label={intl.formatMessage({ id: "chatbot-input-label" })}
          value={input}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setInput(e.target.value)
          }
          onKeyDown={handleKeyDown}
          placeholder={intl.formatMessage({ id: "chatbot-input-placeholder" })}
          disabled={isLoading}
          flex={1}
          resize="none"
          dataTestId="chatbotInput"
        />
        <Button
          variant={ButtonVariant.Primary}
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          dataTestId="chatbotSendButton"
          aria-label={intl.formatMessage({ id: "chatbot-send-label" })}
          icon={<IconChevronRightMedium aria-hidden />}
        />
      </Row>
    </Col>
  );
};
